import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldAlert, Building2, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const SEV = { critical: "#FF3B30", high: "#FF7A45", warning: "#FFD60A", info: "#4da3ff" };

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/workspace/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "This shared dashboard is not available.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const accent = data?.accent_color || "#4da3ff";

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="animate-spin text-[#007AFF]" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <div className="text-center"><ShieldCheck size={40} className="text-zinc-600 mx-auto mb-3" /><p className="text-white font-medium">{error}</p></div>
    </div>
  );

  const counts = data.counts || {};
  const items = data.items || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" style={{ "--ws-accent": accent }}>
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden" style={{ background: `${accent}22` }}>
            {data.logo_url ? <img src={data.logo_url} alt="" className="w-full h-full object-contain" /> : <Building2 size={18} style={{ color: accent }} />}
          </div>
          <div>
            <p className="text-white font-heading font-bold text-lg" data-testid="share-workspace-name">{data.name}</p>
            <p className="text-zinc-500 text-xs">Live status report · {data.domainsTracked} site{data.domainsTracked === 1 ? "" : "s"} monitored</p>
          </div>
          <span className="ml-auto text-[11px] uppercase tracking-wide font-semibold px-2.5 py-1 rounded-full" style={{ color: accent, background: `${accent}1a` }}>Read-only</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8" data-testid="share-view">
        <h1 className="font-heading text-2xl font-bold text-white mb-1">What needs attention</h1>
        <p className="text-zinc-400 text-sm mb-5">A live snapshot of the issues we're monitoring for you.</p>

        <div className="flex flex-wrap gap-2.5 mb-6">
          {["critical", "high", "warning", "info"].map((k) => (
            <div key={k} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: SEV[k] }} />
              <span className="text-white font-heading text-lg font-bold tabular-nums">{counts[k] || 0}</span>
              <span className="text-zinc-500 text-xs uppercase tracking-wide">{k}</span>
            </div>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16 border border-white/10 rounded-xl">
            <CheckCircle2 size={40} className="text-[#32D74B] mx-auto mb-3" />
            <p className="text-white font-medium">Everything looks healthy.</p>
            <p className="text-zinc-500 text-sm mt-1">No issues need attention right now.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="border border-white/10 rounded-lg p-3.5 flex items-start gap-3" style={{ background: `${SEV[it.severity]}0a` }}>
                <ShieldAlert size={16} style={{ color: SEV[it.severity] }} className="mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium">{it.title}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: SEV[it.severity], background: `${SEV[it.severity]}1a` }}>{it.severity}</span>
                  </div>
                  <p className="text-zinc-400 text-xs mt-1 leading-relaxed">{it.detail}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
          <span className="text-zinc-600 text-xs">Powered by Link Website Pro</span>
          <a href="/" className="text-xs font-medium" style={{ color: accent }}>Get your own command center →</a>
        </div>
      </main>
    </div>
  );
}
